import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { CONTACT } from "@/lib/config";
import {
  ArrowRight,
  Phone,
  MessageCircle,
  ShieldCheck,
  Users,
  FileCheck,
  Landmark,
  Clock,
  Sparkles,
  Star,
  ChevronRight,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1717565813196-8944b57877f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBnbGFzcyUyMGJ1aWxkaW5nJTIwYmx1ZSUyMHNreXxlbnwwfHx8fDE3ODM3NDY4MTV8MA&ixlib=rb-4.1.0&q=85";
const STATS_IMG =
  "https://images.unsplash.com/photo-1579445505461-acecf2596190?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBnbGFzcyUyMGJ1aWxkaW5nJTIwYmx1ZSUyMHNreXxlbnwwfHx8fDE3ODM3NDY4MTV8MA&ixlib=rb-4.1.0&q=85";
const ABOUT_IMG =
  "https://images.unsplash.com/photo-1762504351153-58a41cd486dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc4Mzc0NjgwNHww&ixlib=rb-4.1.0&q=85";

function useCounter(target, run, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let start;
    let raf;
    let cancelled = false;
    const step = (ts) => {
      if (cancelled) return;
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setVal(Math.floor(p * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => { cancelled = true; cancelAnimationFrame(raf); };
  }, [target, run, duration]);
  return val;
}

export default function Home() {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({ clients_served: 0, returns_filed: 0, years_experience: 0, cities_covered: 0 });
  const [runCount, setRunCount] = useState(false);

  useEffect(() => {
    api.get("/services?featured=true").then((r) => setServices(r.data.slice(0, 6))).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    const t = setTimeout(() => setRunCount(true), 300);
    return () => clearTimeout(t);
  }, []);

  const c1 = useCounter(stats.clients_served, runCount);
  const c2 = useCounter(stats.returns_filed, runCount);
  const c3 = useCounter(stats.years_experience, runCount);
  const c4 = useCounter(stats.cities_covered, runCount);

  return (
    <PublicLayout>
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 -z-10 bg-slate-950">
          <img src={HERO_IMG} alt="Modern glass tower" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/85 to-blue-950/70" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-28 lg:pt-32 lg:pb-36 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-white">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-1.5 text-xs tracking-widest uppercase text-blue-200"
            >
              <Sparkles size={14} /> Trusted since 2009 • Rajkot
            </motion.div>
            <h1 className="h-display mt-6 text-5xl md:text-6xl lg:text-7xl">
              Precise accounting. <br />
              <span className="text-blue-300 italic">Confident</span> tax filing.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-slate-300 leading-relaxed">
              KK Associates is a Rajkot-based firm helping individuals, MSMEs, startups and NRIs stay compliant, save tax and scale profitably — with senior advisors on every engagement.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/contact" data-testid="hero-book-btn" className="btn-hover inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-6 py-3 text-sm font-medium hover:bg-blue-300">
                Book a Free Consultation <ArrowRight size={16} />
              </Link>
              <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="btn-hover inline-flex items-center gap-2 rounded-full border border-white/25 text-white px-6 py-3 text-sm font-medium hover:bg-white/10">
                <MessageCircle size={16} /> Chat on WhatsApp
              </a>
              <a href={`tel:${CONTACT.phoneRaw}`} data-testid="hero-call-btn" className="btn-hover inline-flex items-center gap-2 rounded-full text-blue-200 px-4 py-3 text-sm hover:text-white">
                <Phone size={16} /> {CONTACT.phone}
              </a>
            </div>
            <div className="mt-10 grid grid-cols-3 max-w-md gap-6 text-slate-200">
              <div><div className="text-2xl font-medium">15+</div><div className="text-xs uppercase tracking-widest text-slate-400">Years</div></div>
              <div><div className="text-2xl font-medium">850+</div><div className="text-xs uppercase tracking-widest text-slate-400">Clients</div></div>
              <div><div className="text-2xl font-medium">22+</div><div className="text-xs uppercase tracking-widest text-slate-400">Services</div></div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-3xl p-6 lg:p-8 backdrop-blur-xl bg-white/8 border border-white/15 shadow-2xl">
              <div className="text-xs uppercase tracking-widest text-blue-200">What we do best</div>
              <div className="mt-4 grid grid-cols-2 gap-3">
                {[
                  { icon: FileCheck, label: "GST & ITR" },
                  { icon: Landmark, label: "Company Setup" },
                  { icon: Users, label: "Payroll & HR" },
                  { icon: ShieldCheck, label: "Audit" },
                  { icon: Clock, label: "ROC Filing" },
                  { icon: Sparkles, label: "Virtual CFO" },
                ].map((x, i) => (
                  <div key={i} className="rounded-xl bg-white/5 border border-white/10 px-4 py-3 flex items-center gap-3 text-white">
                    <x.icon size={18} className="text-blue-300" />
                    <span className="text-sm">{x.label}</span>
                  </div>
                ))}
              </div>
              <Link to="/services" data-testid="hero-see-services" className="mt-6 inline-flex items-center gap-2 text-sm text-blue-200 hover:text-white transition-colors">
                See all services <ChevronRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 -mt-14 relative z-10">
          <div className="rounded-2xl bg-white shadow-xl border border-slate-100 grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-100">
            {[
              { n: c1, label: "Happy Clients", suf: "+" },
              { n: c2, label: "Returns Filed", suf: "+" },
              { n: c3, label: "Years of Practice", suf: "" },
              { n: c4, label: "Cities Served", suf: "+" },
            ].map((s, i) => (
              <div key={i} className="px-6 py-8 text-center">
                <div className="counter-num text-4xl md:text-5xl h-display text-slate-900">{s.n.toLocaleString()}{s.suf}</div>
                <div className="mt-2 text-xs uppercase tracking-widest text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-widest text-blue-700">Why KK Associates</div>
          <h2 className="h-display mt-3 text-4xl md:text-5xl text-slate-900">A partner your business can grow with.</h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            We combine deep Indian tax expertise with modern cloud accounting to give you clean books, timely filings and clear advice — without the jargon.
          </p>
          <Link to="/about" data-testid="why-learn-more" className="mt-8 inline-flex items-center gap-2 text-blue-700 hover:text-blue-900 transition-colors text-sm font-medium">
            Learn more about us <ArrowRight size={14} />
          </Link>
        </div>
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            { t: "Senior-led engagements", d: "A qualified CA reviews every filing — no junior handoffs." },
            { t: "Fixed, transparent pricing", d: "Clear packages with zero hidden charges." },
            { t: "On-time, every time", d: "SLA-based turnaround with proactive reminders." },
            { t: "Data privacy first", d: "Encrypted document vault and NDAs by default." },
          ].map((x, i) => (
            <div key={i} className="rounded-2xl border border-slate-200 p-6 bg-white hover:border-blue-300 hover:shadow-md transition-shadow duration-300">
              <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center mb-4">
                <ShieldCheck size={18} />
              </div>
              <div className="text-base font-medium text-slate-900">{x.t}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-slate-50 border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-blue-700">Our Services</div>
              <h2 className="h-display mt-3 text-4xl md:text-5xl text-slate-900">Everything a growing business needs.</h2>
            </div>
            <Link to="/services" data-testid="services-view-all" className="text-sm text-blue-700 hover:text-blue-900 inline-flex items-center gap-1">View all services <ChevronRight size={14} /></Link>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
              >
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group block rounded-2xl bg-white border border-slate-200 p-6 h-full hover:border-blue-400 hover:-translate-y-0.5 transition-transform duration-300"
                >
                  <div className="text-xs uppercase tracking-widest text-blue-700">{s.category}</div>
                  <h3 className="mt-2 text-xl font-medium text-slate-900 group-hover:text-blue-700 transition-colors">{s.title}</h3>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.short_description}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm text-blue-700">Explore <ArrowRight size={14} /></span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative overflow-hidden bg-slate-950">
        <div className="absolute inset-0 -z-10 bg-slate-950">
          <img src={STATS_IMG} alt="Financial abstract" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/85" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 text-white">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest text-blue-300">How we work</div>
            <h2 className="h-display mt-3 text-4xl md:text-5xl">Four calm steps from chaos to compliance.</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { n: "01", t: "Free discovery call", d: "Tell us where you are and where you want to be." },
              { n: "02", t: "Custom proposal", d: "Fixed-fee plan mapped to your compliance calendar." },
              { n: "03", t: "Onboarding & setup", d: "Secure doc collection, cloud books setup, checklists." },
              { n: "04", t: "Ongoing partnership", d: "Timely filings, monthly MIS, and proactive advice." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-white/15 bg-white/5 p-6">
                <div className="h-display text-4xl text-blue-300">{s.n}</div>
                <div className="mt-3 text-base font-medium">{s.t}</div>
                <p className="mt-2 text-sm text-slate-300">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-xs uppercase tracking-widest text-blue-700">Kind words</div>
        <h2 className="h-display mt-3 text-4xl md:text-5xl text-slate-900 max-w-3xl">Trusted by proprietors, LLPs and Pvt Ltd companies across Gujarat.</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {testimonials.slice(0, 4).map((t) => (
            <div key={t.id} data-testid={`testimonial-${t.id}`} className="rounded-2xl border border-slate-200 p-8 bg-white hover:shadow-lg transition-shadow duration-300">
              <div className="flex items-center gap-1 text-amber-500">
                {Array.from({ length: t.rating }).map((_, i) => <Star key={i} size={16} fill="currentColor" strokeWidth={0} />)}
              </div>
              <p className="mt-5 text-slate-700 leading-relaxed italic">"{t.quote}"</p>
              <div className="mt-6">
                <div className="text-slate-900 font-medium">{t.name}</div>
                <div className="text-sm text-slate-500">{t.role}{t.company ? ` • ${t.company}` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* LATEST BLOG */}
      <LatestBlog />


      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white p-10 md:p-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <img src={ABOUT_IMG} alt="Team" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/85 to-transparent" />
          <div className="relative lg:col-span-8">
            <h3 className="h-display text-3xl md:text-4xl">Ready to hand over your compliance to experts?</h3>
            <p className="mt-3 text-slate-300 max-w-xl">Book a 15-minute call with our senior team. Free, no obligation.</p>
          </div>
          <div className="relative lg:col-span-4 flex flex-wrap gap-3 lg:justify-end">
            <Link to="/contact" data-testid="cta-book" className="btn-hover rounded-full bg-white text-slate-900 px-6 py-3 text-sm font-medium hover:bg-blue-300">Book Consultation</Link>
            <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" data-testid="cta-whatsapp" className="btn-hover rounded-full border border-white/25 text-white px-6 py-3 text-sm font-medium hover:bg-white/10 inline-flex items-center gap-2"><MessageCircle size={16} /> WhatsApp</a>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function LatestBlog() {
  const [blogs, setBlogs] = useState([]);
  useEffect(() => { api.get("/blogs").then((r) => setBlogs(r.data.slice(0, 3))).catch(() => {}); }, []);
  if (blogs.length === 0) return null;
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";
  return (
    <section className="bg-slate-50 border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-700">Latest from the blog</div>
            <h2 className="h-display mt-3 text-4xl md:text-5xl text-slate-900">Insights on tax, compliance & running a better business.</h2>
          </div>
          <Link to="/blog" data-testid="home-blog-all" className="text-sm text-blue-700 hover:text-blue-900 inline-flex items-center gap-1">All articles <ChevronRight size={14} /></Link>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {blogs.map((b) => (
            <Link key={b.id} to={`/blog/${b.slug}`} data-testid={`home-blog-${b.slug}`} className="group block rounded-2xl bg-white border border-slate-200 overflow-hidden hover:border-blue-400 hover:-translate-y-0.5 transition-transform duration-300">
              {b.cover_image && (
                <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                  <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-6">
                <div className="text-xs uppercase tracking-widest text-blue-700">{b.category}</div>
                <h3 className="mt-2 text-lg font-medium text-slate-900 group-hover:text-blue-700 transition-colors">{b.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-2">{b.excerpt}</p>
                <div className="mt-5 text-xs text-slate-500">{fmt(b.published_at || b.created_at)}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
